import type { Virtualizer } from '@tanstack/react-virtual';
import type { JSX } from 'react';
import { useIntl } from 'react-intl';

import { DataTableBody } from './DataTableBody.js';
import { DataTableHeader } from './DataTableHeader.js';
import {
  DataTableContentContextProvider,
  useDataTable,
} from '../contexts/DataTableContext.js';
import { CellActionFloatingMenu } from '../features/UserActions/FloatingMenu.js';
import { dataTableMessages } from '../messages.js';
import { DataTableScrollShadow } from './DataTableScrollShadow.js';
import { getDataTableBorderWidth } from '../../utils/data-table-border-width.js';

/**
 * Renders the content of the `DataTable`.
 *
 * This component is responsible for displaying the header and the main body of the datatable,
 * including rows and columns.
 */
export const DataTableContent = (props: {
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
}): JSX.Element | null => {
  const intl = useIntl();
  const table = useDataTable();

  const visibleLeafColumnsWithoutBuiltin =
    table.getVisibleLeafColumnsWithoutBuiltin();
  const hasVisibleColumns = visibleLeafColumnsWithoutBuiltin.length > 0;

  if (!hasVisibleColumns) {
    return null;
  }

  const { rowVirtualizer, columnVirtualizer } = props;

  const rows = table.getRowModel().rows;

  const draggingRow = table.getState().draggingRowInfo;
  const isDraggingRow = !!draggingRow;

  const isDataEmpty = rowVirtualizer.options.count === 0;
  const isColumnsEmpty = columnVirtualizer.options.count === 0;
  const isEmpty = isDataEmpty || isColumnsEmpty;

  // We need to detect overflow on either side, so the scroll shadow can be displayed only if the scrollable content is actually overflowing
  const scrollOffset = columnVirtualizer.scrollOffset ?? 0;
  const scrollElement = columnVirtualizer.scrollElement;
  const tableViewportWidth = scrollElement?.clientWidth ?? 0;
  const scrollElementWidth = scrollElement?.scrollWidth ?? 0;

  const visualVariantOptions = table.getVisualVariantOptions();
  const hasOverflowLeft = scrollOffset > 0;

  const gridHeight = isEmpty ? 'auto' : rowVirtualizer.getTotalSize();
  const borderWidth = visualVariantOptions.contained
    ? getDataTableBorderWidth()
    : 0;
  const hasOverflowRight =
    scrollElementWidth - scrollOffset - borderWidth > tableViewportWidth;

  const cellActionAriaLabel = intl.formatMessage(
    dataTableMessages.cellActionLabel,
  );

  return (
    <div
      role="grid"
      style={{
        display: 'grid',
        height: gridHeight,
        gridTemplateColumns: table.getGridTemplateColumns(),
        gridTemplateRows: table.getGridTemplateRows(),
      }}
      onMouseOver={(event) => {
        if (!isDraggingRow) {
          table.handleInteractiveRowMouseOver(event);
        }
      }}
      onMouseLeave={(event) => {
        table.handleInteractiveRowMouseLeave(event);
      }}
      onMouseDown={(event) => {
        if (
          // main button (left button)
          event.button === 0 ||
          // auxiliary button (wheel/middle button)
          event.button === 1
        ) {
          table.handleInteractiveRowClick(event);
        }
      }}
      onContextMenu={(event) => {
        // disable context menu for links, we need to show the browser menu instead
        if (event.target instanceof Element && event.target.closest('a')) {
          return;
        }

        table.getUserActionFloatingMenuContextMenuHandler()(event);
      }}
    >
      <DataTableContentContextProvider
        value={{
          hasOverflowLeft,
          hasOverflowRight,
          cellActionAriaLabel,
        }}
      >
        {visualVariantOptions.headers === 'default' && <DataTableHeader />}
        <DataTableBody
          rows={rows}
          rowVirtualizer={rowVirtualizer}
          columnVirtualizer={columnVirtualizer}
        />
        {(table.options.enableCellActions ||
          table.options.enableColumnActions ||
          table.options.enableSorting) && <CellActionFloatingMenu />}
      </DataTableContentContextProvider>
      <DataTableScrollShadow
        hasOverflowLeft={hasOverflowLeft}
        hasOverflowRight={hasOverflowRight}
      />
    </div>
  );
};
